import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Combobox } from "@/components/ui/form-kit";
import { Hash } from "lucide-react";

// Managed CSI / budget cost-code list (standard + custom), fetched once and cached
// module-wide so estimators pick from the workspace list instead of typing free text.
let _cache = null;
let _inflight = null;

export function useCostCodes() {
  const [codes, setCodes] = useState(_cache || []);
  useEffect(() => {
    let alive = true;
    if (_cache) { setCodes(_cache); return; }
    if (!_inflight) {
      _inflight = api.get("/cost-codes")
        .then((r) => { _cache = [...(r.data.standard || []), ...(r.data.custom || [])]; return _cache; })
        .catch(() => { _cache = []; return _cache; });
    }
    _inflight.then((c) => { if (alive) setCodes(c || []); });
    return () => { alive = false; };
  }, []);
  return codes;
}

export function CostCodeSelect({ value, onChange, disabled, className = "", placeholder = "— none —", inherit, testid }) {
  const codes = useCostCodes();
  const val = value || "";
  const known = codes.some((c) => c.code === val);
  return (
    <select value={val} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={className}>
      <option value="">{inherit || placeholder}</option>
      {!known && val && <option value={val}>{val} — (custom)</option>}
      {codes.map((c) => (
        <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
      ))}
    </select>
  );
}

// Pick-or-type cost code: a managed combobox (search by code OR name) that still accepts a
// brand-new custom code typed by the user. Stores the bare code; shows "code — name" in the list.
export function CostCodeCombobox({ value, onChange, disabled, className = "", placeholder = "— none —", inherit, testid }) {
  const codes = useCostCodes();
  const options = codes.map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` }));
  return (
    <Combobox value={value || ""} onChange={onChange} options={options} disabled={disabled}
      placeholder={inherit || placeholder} testid={testid} className={className} icon={Hash} />
  );
}
